import React, { useState, useEffect, useRef } from 'react';
import * as Diff from 'diff'; // Corrected alias to singular Diff
import { useTheme } from '../../../context/ThemeContext'; // Import useTheme
// @ts-ignore
import { Helmet } from 'react-helmet';

// Helper to escape HTML special characters
const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// Updated generateDiffHtml to be theme-aware
const generateDiffHtml = (text1Value: string, text2Value: string, forText1View: boolean, isDarkMode: boolean): string => {
  const differences = Diff.diffWords(text1Value, text2Value);
  let html = '';

  // Define theme-based highlight colors
  const styles = {
    light: {
      removedBg: '#ffeeee',
      removedText: '#a60000',
      addedBg: '#e6ffed',
      addedText: '#005200',
    },
    dark: {
      removedBg: '#5c1f1f',
      removedText: '#ffabab',
      addedBg: '#1e4620',
      addedText: '#a3ffac',
    },
  };
  const currentStyle = isDarkMode ? styles.dark : styles.light;

  // Explicitly type 'part' using Diff.Change from the @types/diff package
  differences.forEach((part: Diff.Change) => { 
    const escapedValue = escapeHtml(part.value);
    const valueWithBreaks = escapedValue.replace(/\\r\\n|\\r|\\n/g, '<br>');

    if (forText1View) {
      if (part.removed) {
        html += `<span style="background-color: ${currentStyle.removedBg}; color: ${currentStyle.removedText};">${valueWithBreaks}</span>`;
      } else if (!part.added) {
        html += `<span>${valueWithBreaks}</span>`;
      }
    } else {
      if (part.added) {
        html += `<span style="background-color: ${currentStyle.addedBg}; color: ${currentStyle.addedText};">${valueWithBreaks}</span>`;
      } else if (!part.removed) {
        html += `<span>${valueWithBreaks}</span>`;
      }
    }
  });
  return html;
};

const TextDiff: React.FC = () => {
  const { isDarkMode } = useTheme(); // Get theme status
  const [inputText1, setInputText1] = useState<string>('');
  const [inputText2, setInputText2] = useState<string>('');

  // These states will now hold the HTML to be rendered in the contentEditable divs
  const [displayHtml1, setDisplayHtml1] = useState<string>('');
  const [displayHtml2, setDisplayHtml2] = useState<string>('');

  const debounceDelay = 300; // milliseconds

  // Refs for the contentEditable divs to manage focus and selection if needed (advanced)
  const editor1Ref = useRef<HTMLDivElement>(null);
  const editor2Ref = useRef<HTMLDivElement>(null);

  // Function to get plain text from HTML (simplified version)
  const getPlainText = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html.replace(/<br\s*\/?>/gi, '\\n'); // Convert <br> back to newlines
    return tempDiv.textContent || tempDiv.innerText || '';
  };
  
  // Generate and set HTML for display based on plain text inputs
  useEffect(() => {
    const timerId = setTimeout(() => {
      // When generating HTML for contentEditable, we always start from plain text
      // The input to generateDiffHtml should be the plain text inputText1 and inputText2
      setDisplayHtml1(generateDiffHtml(inputText1, inputText2, true, isDarkMode));
      setDisplayHtml2(generateDiffHtml(inputText1, inputText2, false, isDarkMode));
    }, debounceDelay);

    return () => clearTimeout(timerId);
  }, [inputText1, inputText2, debounceDelay, isDarkMode]);

  const handleInput = (
    e: React.FormEvent<HTMLDivElement>, 
    setTextState: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const plainText = (e.currentTarget as HTMLDivElement).innerText; 
    // When user types, we update the plain text state.
    // useEffect will then regenerate the highlighted HTML.
    setTextState(plainText);
  };

  const clearText1 = () => {
    setInputText1('');
    setDisplayHtml1(''); // Clear HTML display
  };

  const clearText2 = () => {
    setInputText2('');
    setDisplayHtml2(''); // Clear HTML display
  };

  // Updated sharedEditableStyles inspired by JsonUtility
  const sharedEditableStyles = `w-full h-64 p-3 border rounded-md shadow-sm 
                             font-mono whitespace-pre-wrap break-words overflow-auto min-h-[4em] 
                             text-sm 
                             bg-gray-50 dark:bg-gray-700 
                             text-gray-900 dark:text-white 
                             border-gray-300 dark:border-gray-600 
                             focus:ring-blue-500 focus:border-blue-500 focus:ring-1`;
  
  // Define legend colors based on theme for consistency (though legend is being removed)
  const legendStyles = {
    light: {
      removedBg: '#ffeeee',
      removedBorder: '#a60000',
      addedBg: '#e6ffed',
      addedBorder: '#005200',
    },
    dark: {
      removedBg: '#5c1f1f',
      removedBorder: '#ffabab',
      addedBg: '#1e4620',
      addedBorder: '#a3ffac',
    },
  };
  const currentLegendStyle = isDarkMode ? legendStyles.dark : legendStyles.light;

  const editableDivStyles = `w-full p-3 border rounded-md shadow-sm 
                            font-mono whitespace-pre-wrap break-words overflow-auto 
                            text-sm 
                            bg-white dark:bg-gray-700 
                            text-gray-900 dark:text-white 
                            border-gray-300 dark:border-gray-600 
                            focus:ring-blue-500 focus:border-blue-500 focus:ring-1 
                            min-h-[calc(100vh-250px)] md:min-h-[calc(100vh-200px)]`; // Adjusted height

  return (
    <>
      <Helmet>
        <title>Text Diff Checker | Compare Texts Online - Davinqi</title>
        <meta name="description" content="Compare two texts and highlight the differences with Davinqi Text Diff Checker. Fast, privacy-friendly, and easy to use." />
        <meta property="og:title" content="Text Diff Checker | Compare Texts Online - Davinqi" />
        <meta property="og:description" content="Compare two texts and highlight the differences with Davinqi Text Diff Checker. Fast, privacy-friendly, and easy to use." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/text/diff" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Text Diff Checker | Compare Texts Online - Davinqi" />
        <meta name="twitter:description" content="Compare two texts and highlight the differences with Davinqi Text Diff Checker. Fast, privacy-friendly, and easy to use." />
      </Helmet>
      <div className="pt-0 pb-4 px-4 max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col h-full">
        <div className="text-center mb-2 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Text Difference Checker
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Compare two texts and highlight the differences. Paste your text (direct typing not supported).
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-grow">
          <div className="flex flex-col h-full">
            <div
              ref={editor1Ref}
              contentEditable={true}
              onInput={(e) => handleInput(e, setInputText1)}
              dangerouslySetInnerHTML={{ __html: displayHtml1 }}
              className={`${editableDivStyles} flex-grow`}
              aria-label="Original Text Input Area"
              suppressContentEditableWarning={true}
            />
          </div>
          <div className="flex flex-col h-full">
            <div
              ref={editor2Ref}
              contentEditable={true}
              onInput={(e) => handleInput(e, setInputText2)}
              dangerouslySetInnerHTML={{ __html: displayHtml2 }}
              className={`${editableDivStyles} flex-grow`}
              aria-label="Changed Text Input Area"
              suppressContentEditableWarning={true}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TextDiff; 